import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, Mail, MapPin } from "lucide-react";
import AnalyticsTracker from "@/components/AnalyticsTracker";
const Layout = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigation = [{
    name: "Accueil",
    href: "/"
  }, {
    name: "Présentation",
    href: "/presentation"
  }, 
  // {
  //   name: "Réservation",
  //   href: "/reservation"
  // }, 
  {
    name: "Références",
    href: "/references"
  }, {
    name: "Contact",
    href: "/contact"
  }];
  const isActive = (href: string) => location.pathname === href;
  return <div className="min-h-screen bg-background">
      {/* Analytics Tracker */}
      <AnalyticsTracker pageTitle={document.title} />
      
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1996325666971779072_BT CONNECT BLACK.png?image_process=quality,q_90/resize,w_1280/format,webp" alt="BT-Connect SARL" className="w-auto sk-edit-loading h-10 object-cover" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navigation.map(item => <Link key={item.name} to={item.href} className={`text-sm font-medium transition-colors hover:text-primary ${isActive(item.href) ? "text-primary border-b-2 border-primary pb-1" : "text-muted-foreground"}`}>
                  {item.name}
                </Link>)}
            </nav>

            {/* Contact Info */}
            <div className="hidden lg:flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Phone className="h-4 w-4" />
                <span>026 660 45 88</span>
              </div>
              <div className="flex items-center space-x-1">
                <Mail className="h-4 w-4" />
                <span>info@bt-connect.ch</span>
              </div>
            </div>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-4 mt-8">
                  {navigation.map(item => <Link key={item.name} to={item.href} onClick={() => setIsOpen(false)} className={`text-lg font-medium transition-colors hover:text-primary ${isActive(item.href) ? "text-primary" : "text-muted-foreground"}`}>
                      {item.name}
                    </Link>)}
                  <div className="pt-4 border-t space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>026 660 45 88</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>079 614 84 79</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>info@bt-connect.ch</span>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center">
                <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2000819461439729664_BT CONNECT BLACK.png?image_process=quality,q_90/resize,w_1280/format,webp" alt="BT-Connect SARL" className="w-auto filter invert sk-edit-loading h-10 object-cover" />
              </div>
              <p className="text-sm text-primary-foreground/80">
                Votre partenaire technologique pour l'audiovisuel, la domotique et les solutions réseau.
              </p>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h3 className="font-semibold">Nos Services</h3>
              <ul className="space-y-2 text-sm text-primary-foreground/80">
                <li>Audiovisuel & Multimédia</li>
                <li>Sonorisation</li>
                <li>Domotique</li>
                <li>Éclairage Scénique</li>
                <li>Vidéo Surveillance</li>
                <li>Fibre Optique</li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold">Contact</h3>
              <div className="space-y-2 text-sm text-primary-foreground/80">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>026 660 45 88</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>079 614 84 79</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>info@bt-connect.ch</span>
                </div>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 mt-0.5" />
                  <span>Rue d'Yverdon 27<br />1530 Payerne, Suisse</span>
                </div>
              </div>
            </div>

            {/* Legal */}
            <div className="space-y-4">
              <h3 className="font-semibold">Informations</h3>
              <div className="space-y-2 text-sm text-primary-foreground/80">

                <p>© 2024 BT-Connect SARL</p>
                <p>Tous droits réservés</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Layout;